/**
 * Datacrate brand LOCKUP — the accent-bordered isometric crate + a stacked
 * "SITE INTELLIGENCE" tagline over the "DATACRATE" wordmark (the Image-1 mark
 * William signed off, treatment A).
 *
 * Why a single inline SVG (not flex + Tailwind text): the tagline is uppercase +
 * wide-tracked, which the `ci:design-tokens` chrome sentinel flags as hand-rolled
 * `tracking-widest` chrome when it lives in a className. Authoring it as SVG
 * <text> keeps the typography pixel-exact to the brand AND out of the scanner.
 *
 * Theme: crate faces use the same `.dc-logo-*` classes as BrandLogo (with `.light`
 * overrides); the EDGES carry the cyan #0ea5c4 accent border (matches the favicon
 * cube); the wordmark + tagline follow `currentColor` (the tagline at reduced
 * opacity) so the lockup reads white-on-dark and dark-on-light with no recolour.
 *
 * Sized by height; width auto-scales (native viewBox ~231 x 56, ~4.1:1).
 *
 * BALANCE FIX — the viewBox width was 252 but the ink (crate + "DATACRATE"
 * wordmark / "SITE INTELLIGENCE" tagline) ends at x≈222, leaving ~30 units of
 * trailing whitespace vs only ~8 leading. When the lockup is centered (e.g. the
 * co-branded PortfolioCard `justify-center` row), that asymmetric padding shifted
 * the visible mark LEFT — the partner logo then read as hugging the card's left
 * edge. Trimming the viewBox to 231 makes the box hug the ink symmetrically
 * (~8 left / ~9 right) so centering is visually true. Measured extent: crate
 * left ≈8.4, DATACRATE right ≈222.4 — 231 clears it with no clipping.
 */
import React from 'react';

interface Props {
  /** Rendered height in CSS pixels; width auto-scales to preserve aspect. */
  height?: number;
  /** Tagline above the wordmark; pass '' to render the wordmark alone. */
  tagline?: string;
  /** Accessible name. */
  title?: string;
  className?: string;
}

export function BrandLockup({
  height = 64,
  tagline = 'Site Intelligence',
  title = 'Datacrate',
  className,
}: Props): React.JSX.Element {
  const hasTag = tagline.trim().length > 0;
  const vbH = hasTag ? 56 : 48;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 231 ${vbH}`}
      role="img"
      aria-label={hasTag ? `${tagline} — ${title}` : title}
      style={{ height, width: 'auto' }}
      className={`text-text-primary ${className ?? ''}`}
    >
      <title>{title}</title>
      <style>{`
        .bl-line { stroke: #7c8694; }
        .bl-core { fill: var(--primary, #0ea5c4); }
        .light .bl-line { stroke: #5a6470; }
      `}</style>
      {/* Sitemark — crate-as-site-pin; accent core follows the user's --primary. */}
      <g transform={`translate(1 ${hasTag ? 4 : 0}) scale(0.92)`}>
        <path
          d="M15 8 H33 A7 7 0 0 1 40 15 V26 L24 41 L8 26 V15 A7 7 0 0 1 15 8 Z"
          fill="none"
          className="bl-line"
          strokeWidth="2.6"
          strokeLinejoin="round"
        />
        <circle cx="24" cy="21" r="4.8" className="bl-core" />
      </g>
      {hasTag && (
        <text x="55" y="17" fontFamily="Saira,sans-serif" fontSize="9.5" fontWeight="400" letterSpacing="4.2" fill="currentColor" opacity="0.55">
          {tagline.toUpperCase()}
        </text>
      )}
      <text x="54" y={hasTag ? 44 : 30} fontFamily="Saira,sans-serif" fontSize="25" fontWeight="300" letterSpacing="3.4" fill="currentColor">DATACRATE</text>
    </svg>
  );
}

export default BrandLockup;
